<template>
  <div id="app" class="product-page">
    <nav-bar :user="currentUser" :isAdmin="userIsAdmin" @logout="handleLogout" />
    
    <div>
      <router-view />
    </div>

    <!-- Transition alert for no more unreviewed data -->
    <transition name="fade">
      <div v-if="showNoMoreUnreviewed" class="alert-banner">
        No more unreviewed data left!
      </div>
    </transition>

    <!-- Snackbar for feedback messages -->
    <div v-if="showSnackbar" :class="['snackbar', snackbarColor]">
      {{ snackbarMessage }}
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/shared/NavBar.vue';
import { getAuth, signOut, onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { ref, onMounted } from "vue";
import { useProductStore } from '@/store/product-viewer/productStore';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';

export default {
  name: 'App',
  components: {
    NavBar
  },
  setup() {
    const auth = getAuth();
    const router = useRouter();
    const route = useRoute();
    const store = useProductStore();
    const currentUser = ref(null);
    const userIsAdmin = ref(false);
    const userIsWrite = ref(false);
    const showNoMoreUnreviewed = ref(false);
    const showSnackbar = ref(false);
    const snackbarMessage = ref('');
    const snackbarColor = ref('success');

    const handleLogout = async () => {
      await signOut(auth);      
      currentUser.value = null;
      router.push('/login');
    };

    const showFeedback = (msg, color = 'success') => {
      snackbarMessage.value = msg;
      snackbarColor.value = color;
      showSnackbar.value = true;
      setTimeout(() => {
        showSnackbar.value = false;
      }, 2000);
    };

    onMounted(async () => {
      onAuthStateChanged(auth, async (user) => {
        currentUser.value = user;      
        if (user) {
          const tokenResult = await getIdTokenResult(user);
          userIsAdmin.value = tokenResult.claims['data-manager-role'] === 'admin';
          userIsWrite.value = tokenResult.claims['data-manager-role'] === 'write';
          store.setAuthState(user, userIsAdmin.value, userIsWrite.value);
        }
      });
    });

    return {
      currentUser,
      userIsAdmin,
      userIsWrite,
      showNoMoreUnreviewed,
      showSnackbar,
      snackbarMessage,
      snackbarColor,
      handleLogout,
      showFeedback,
    };
  }
}
</script>

<style scoped>
.product-page {
  font-family: 'Inter', sans-serif;
  color: #2c3e50;
  background-color: #ffffff;
}

.alert-banner {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffb200;
  color: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 9999;
}

.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.snackbar.success {
  background: #28a745;
}

.snackbar.error {
  background: #dc3545;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

